import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { updateTask } from '@/features/preinvest/tasks/api'
import { toast } from '@/lib/toast'
import { toastUndo } from '@/lib/toastUndo'
import { cn } from '@/lib/utils'
import { overdueText, shortDay, weekdayDay } from '../record/format'
import { getPlanned } from './api'
import type { PlannedTask, RelationRef, UpcomingDeadline } from './types'

interface Loaded {
  scope: string
  tasks: PlannedTask[]
  deadlines: UpcomingDeadline[]
  status: 'ready' | 'error'
}

/** "vr 16 okt", or for an overdue task "! 9 okt · 8 dagen te laat" (never colour alone, IA-20). */
function dueText(task: PlannedTask): string | null {
  if (!task.due_date) return null
  if (!task.overdue) return weekdayDay(task.due_date)
  return [`! ${shortDay(task.due_date)}`, overdueText(task.due_date)].filter(Boolean).join(' · ')
}

function deadlineText(deadline: UpcomingDeadline): string {
  const due = shortDay(deadline.due_on)
  const from = `taak vanaf ${shortDay(deadline.task_from)}`
  return deadline.kind === 'einde'
    ? `${deadline.title} · herinnering ${due} · ${from}`
    : `${deadline.title} · uiterlijk ${due} · ${from}`
}

/**
 * "Gepland": the open tasks of a relation, the earliest due date first (the forward-looking
 * part of the timeline, ED-03). A task is ticked off here with "Ongedaan maken"; deleting
 * tasks happens in Mijn taken. `showPerson` names the contact on an organisation page.
 * Below the tasks come the ◇ lines: deadlines of agreements that have no task yet (design §4.5).
 */
export function PlannedSection({
  relation,
  version,
  onChanged,
  showPerson = false,
}: {
  relation: RelationRef
  version: number
  onChanged: () => void
  showPerson?: boolean
}) {
  const { kind, id } = relation
  const scope = `${kind}:${id}`
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  // Ticked off here and on their way to the server: hidden until the next answer drops them.
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set())
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    let cancelled = false
    getPlanned({ kind, id })
      .then((planned) => {
        if (cancelled) return
        setLoaded({ scope, tasks: planned.tasks, deadlines: planned.upcoming_deadlines, status: 'ready' })
        // An answer that still lists a ticked-off task was worked out before it was saved.
        setHidden((current) => new Set(planned.tasks.filter((task) => current.has(task.id)).map((task) => task.id)))
      })
      .catch(() => {
        if (!cancelled) setLoaded({ scope, tasks: [], deadlines: [], status: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [kind, id, scope, version, retry])

  const view = loaded !== null && loaded.scope === scope ? loaded : null
  const tasks = (view?.tasks ?? []).filter((task) => !hidden.has(task.id))
  const deadlines = view?.deadlines ?? []

  function setHiddenTask(taskId: string, isHidden: boolean) {
    setHidden((current) => {
      const next = new Set(current)
      if (isHidden) next.add(taskId)
      else next.delete(taskId)
      return next
    })
  }

  async function complete(task: PlannedTask) {
    setHiddenTask(task.id, true)
    try {
      await updateTask(task.id, { status: 'done' })
    } catch (caught) {
      setHiddenTask(task.id, false)
      toast.error(caught instanceof Error && caught.message ? caught.message : 'Taak afronden mislukt.')
      return
    }
    onChanged()
    toastUndo('Taak afgerond.', async () => {
      await updateTask(task.id, { status: 'open' })
      setHiddenTask(task.id, false)
      onChanged()
    })
  }

  return (
    <section aria-label="Gepland" className="vonk-card flex flex-col gap-2 p-5">
      <h2 className="font-heading text-base font-semibold">
        {view?.status === 'ready' ? `Gepland (${tasks.length + deadlines.length})` : 'Gepland'}
      </h2>
      {view === null ? (
        <p className="text-sm text-muted-foreground">Laden…</p>
      ) : view.status === 'error' ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-muted-foreground">Gepland laden mislukt.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setLoaded(null)
              setRetry((count) => count + 1)
            }}
          >
            Opnieuw proberen
          </Button>
        </div>
      ) : tasks.length === 0 && deadlines.length === 0 ? (
        <p className="text-sm text-muted-foreground">Niets gepland.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {tasks.map((task) => {
            const due = dueText(task)
            const names = task.assignees.map((assignee) => assignee.name).join(', ')
            return (
              <li key={task.id} className="flex items-start gap-3 py-2">
                <button
                  type="button"
                  aria-label={`Taak '${task.title}' afronden`}
                  onClick={() => void complete(task)}
                  className="mt-0.5 grid size-5 shrink-0 place-items-center rounded border border-border text-muted-foreground hover:bg-muted hover:text-foreground max-md:size-11"
                >
                  <Check className="size-3.5" aria-hidden />
                </button>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-sm font-medium break-words">{task.title}</span>
                  {showPerson && task.person ? (
                    <span className="text-xs text-muted-foreground">met {task.person.name}</span>
                  ) : null}
                </div>
                {due ? (
                  <span
                    className={cn(
                      'shrink-0 text-xs',
                      task.overdue ? 'font-semibold text-destructive' : 'text-muted-foreground',
                    )}
                  >
                    {due}
                  </span>
                ) : null}
                {task.assignees.length > 0 ? (
                  <span className="flex shrink-0 -space-x-1" title={`Toegewezen aan ${names}`}>
                    {task.assignees.map((assignee) => (
                      <EntityAvatar key={assignee.id} kind="person" name={assignee.name} size="xs" />
                    ))}
                    <span className="sr-only">Toegewezen aan {names}</span>
                  </span>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
      {view?.status === 'ready' && deadlines.length > 0 ? (
        <ul className={cn('flex flex-col gap-1', tasks.length > 0 && 'border-t border-border pt-2')}>
          {deadlines.map((deadline) => (
            <li key={`${deadline.agreement_id}:${deadline.kind}:${deadline.due_on}`} className="flex items-start gap-2 text-sm">
              <span aria-hidden className="shrink-0 text-muted-foreground">
                ◇
              </span>
              <span className="break-words">{deadlineText(deadline)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
